/**
 * Configuration helpers for Pulse SRE Investigation (Phase A).
 */

import type { SreInvestigationMode } from './types';

const trimEnv = (value: string | undefined): string => (value ?? '').trim();

/**
 * Pulse API base (used for BFF path `/api/v1/sre`).
 */
export function getPulseApiBaseUrl(): string {
  return (
    trimEnv(process.env.REACT_APP_API_URL) || 'http://localhost:3002/api/v1'
  ).replace(/\/$/, '');
}

/**
 * Live SRE Agent / investigation API base (SSE + REST).
 *
 * Cutover (T-013): defaults to Pulse BFF `/api/v1/sre` when live is configured.
 * Set `REACT_APP_SRE_USE_BFF=false` to call the agent URL directly (local debug).
 *
 * Live is considered configured when:
 * - `REACT_APP_SRE_USE_BFF=true`, or
 * - `REACT_APP_SRE_INVESTIGATION_API_URL` is set (migrated through BFF by default)
 */
export function getSreInvestigationApiUrl(): string {
  const direct = trimEnv(process.env.REACT_APP_SRE_INVESTIGATION_API_URL);
  const useBffFlag = trimEnv(process.env.REACT_APP_SRE_USE_BFF).toLowerCase();
  const forceDirect = useBffFlag === 'false' || useBffFlag === '0';
  const forceBff = useBffFlag === 'true' || useBffFlag === '1';

  if (forceDirect) {
    return direct;
  }

  const liveConfigured = forceBff || !!direct;
  if (!liveConfigured) {
    return '';
  }

  // Default cutover path: same-origin Pulse BFF → SRE_AGENT_URL
  return `${getPulseApiBaseUrl()}/sre`;
}

/**
 * Demo mode:
 * - Explicitly enabled when REACT_APP_SRE_INVESTIGATION_DEMO=true (wins over API URL — for local demos)
 * - Explicitly disabled when =false
 * - Default: enabled when no live API URL is configured
 */
export function isSreInvestigationDemoForced(): boolean {
  const flag = trimEnv(process.env.REACT_APP_SRE_INVESTIGATION_DEMO).toLowerCase();
  if (flag === 'true' || flag === '1') return true;
  if (flag === 'false' || flag === '0') return false;
  return !getSreInvestigationApiUrl();
}

export function getSreInvestigationMode(): SreInvestigationMode {
  const flag = trimEnv(process.env.REACT_APP_SRE_INVESTIGATION_DEMO).toLowerCase();
  // Explicit demo wins so local demos are reliable even if an API URL is also set
  if (flag === 'true' || flag === '1') return 'demo';

  const apiUrl = getSreInvestigationApiUrl();
  if (apiUrl) return 'live';

  if (flag === 'false' || flag === '0') return 'disabled';

  // No flag + no API → demo (offline-friendly default)
  return 'demo';
}

export function isSreInvestigationAvailable(): boolean {
  return getSreInvestigationMode() !== 'disabled';
}

/** Ticket states where investigation CTA is enabled by default. */
export const INVESTIGATABLE_STATES = new Set([
  'new',
  'open',
  'in_progress',
  'work_in_progress',
  'assigned',
  'active',
  'acknowledged',
  'investigating',
]);

export function normalizeTicketState(state: string | undefined | null): string {
  return (state || '').trim().toLowerCase().replace(/\s+/g, '_');
}

export function isInvestigatableState(state: string | undefined | null): boolean {
  if (!state) return true;
  return INVESTIGATABLE_STATES.has(normalizeTicketState(state));
}

export const SSE_EVENT = {
  thought: 'thought',
  toolStart: 'tool_start',
  toolEnd: 'tool_end',
  toolStarted: 'tool_started',
  toolCompleted: 'tool_completed',
  result: 'result',
  report: 'report',
  error: 'error',
  agentCompleted: 'agent_completed',
  runStarted: 'run_started',
} as const;
